// Tile-based map system
import { TiledMapLoader } from './loaders/TiledMapLoader.js';

export class TileMap {
    constructor(worldWidth, worldHeight, tileSize, tiledData = null) {
        this.tileSize = tileSize;
        this.tiles = [];
        
        // Tileset information (for rendering from image)
        this.tilesets = [];
        this.tilesetImages = new Map(); // Map of tileset name -> Image object

        // Tile types (for collision/walkability - still needed)
        this.tileTypes = {
            floor: { color: '#CCCCCC', walkable: true },
            grass: { color: '#8FBC8F', walkable: true },
            wall: { color: '#333333', walkable: false },
            tree: { color: '#228B22', walkable: true }, // Changed to bush - walkable
            border: { color: '#555555', walkable: false }
        };

        if (tiledData) {
            // Note: loadFromTiled is async, but constructor can't be async
            // So we'll call it separately after construction
            this.tiledData = tiledData;
        } else {
            this.cols = Math.ceil(worldWidth / tileSize);
            this.rows = Math.ceil(worldHeight / tileSize);
            this.generateMap();
        }
    }
    
    /**
     * Initialize tiles from Tiled data (call after construction if tiledData was provided)
     */
    async initFromTiled() {
        if (this.tiledData) {
            await this.loadFromTiled(this.tiledData);
            this.tiledData = null; // Clear after loading
        }
    }

    /**
     * Load map from Tiled JSON data
     * @param {Object} tiledData - Parsed Tiled map data from TiledMapLoader
     */
    async loadFromTiled(tiledData) {
        this.cols = tiledData.width;
        this.rows = tiledData.height;
        
        console.log(`Loading tiles from Tiled: ${this.cols}x${this.rows} tiles, ${tiledData.layers?.length || 0} layers, ${tiledData.tilesets?.length || 0} tilesets`);
        
        // Store tileset information
        this.tilesets = tiledData.tilesets || [];
        
        // Load tileset images
        await this.loadTilesetImages();
        
        // Initialize tiles array
        for (let row = 0; row < this.rows; row++) {
            this.tiles[row] = [];
            for (let col = 0; col < this.cols; col++) {
                this.tiles[row][col] = {
                    type: 'grass', // Default
                    gid: 0, // Store GID for rendering from tileset
                    x: col * this.tileSize,
                    y: row * this.tileSize
                };
            }
        }

        // Load tile layers (process in order, later layers override earlier ones)
        let layersProcessed = 0;
        for (const layer of tiledData.layers) {
            console.log(`Checking layer: name="${layer.name}", type="${layer.type}", visible=${layer.visible}`);
            if (layer.type === 'tilelayer' && layer.visible !== false) {
                console.log(`Loading tile layer: "${layer.name}", data length: ${layer.data?.length || 0}`);
                this.loadTileLayer(layer, tiledData.tilesets);
                layersProcessed++;
            } else {
                console.log(`Skipping layer "${layer.name}": type=${layer.type}, visible=${layer.visible}`);
            }
        }
        console.log(`Processed ${layersProcessed} tile layers`);
    }
    
    /**
     * Load all tileset images
     */
    async loadTilesetImages() {
        const loadPromises = this.tilesets.map(tileset => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    this.tilesetImages.set(tileset.name, img);
                    console.log(`Loaded tileset image: ${tileset.image}`);
                    resolve();
                };
                img.onerror = () => {
                    console.warn(`Failed to load tileset image: ${tileset.image}`);
                    resolve(); // Continue even if image fails to load
                };
                // Use relative path - tileset images are in the same assets directory as the map
                // Remove any leading path from tileset.image (Tiled might include "assets/" or "../assets/")
                let imagePath = tileset.image;
                // Remove common path prefixes that Tiled might add
                if (imagePath.startsWith('../assets/')) {
                    imagePath = imagePath.replace('../assets/', '');
                } else if (imagePath.startsWith('assets/')) {
                    imagePath = imagePath.replace('assets/', '');
                } else if (imagePath.startsWith('src/assets/')) {
                    imagePath = imagePath.replace('src/assets/', '');
                }
                // Use relative path from current page location (works from any directory)
                // No leading dot - just relative to index.html location
                img.src = `assets/${imagePath}`;
            });
        });
        
        await Promise.all(loadPromises);
    }

    /**
     * Load a single tile layer from Tiled data
     * @param {Object} layer - Tile layer data
     * @param {Array} tilesets - Tileset definitions
     */
    loadTileLayer(layer, tilesets) {
        const data = layer.data; // Array of GIDs (Global Tile IDs)
        
        if (!data || !Array.isArray(data)) {
            console.error(`Tile layer "${layer.name}" has no data array!`);
            return;
        }
        
        let tilesSet = 0;
        for (let row = 0; row < layer.height; row++) {
            for (let col = 0; col < layer.width; col++) {
                const index = row * layer.width + col;
                const gid = data[index];
                
                if (gid === 0) continue; // Empty tile
                
                // Get tile type from GID
                const tileType = TiledMapLoader.getTileTypeFromGID(gid, tilesets);
                
                // Map tileset tile type to our internal tile type
                // First try custom properties, then fallback to GID-based mapping
                let internalType = 'grass';
                
                // Check if we got a direct type name from custom properties
                if (this.tileTypes[tileType]) {
                    internalType = tileType;
                } else {
                    // Fallback: Map by GID (assuming standard order: 1=grass, 2=floor, 3=wall, 4=border, 5=tree)
                    // Find which tileset this GID belongs to
                    for (let i = tilesets.length - 1; i >= 0; i--) {
                        const tileset = tilesets[i];
                        if (gid >= tileset.firstgid) {
                            const localId = gid - tileset.firstgid;
                            
                            // Map local ID to tile type (assuming order: 0=grass, 1=floor, 2=wall, 3=border, 4=tree)
                            switch (localId) {
                                case 0: internalType = 'grass'; break;
                                case 1: internalType = 'floor'; break;
                                case 2: internalType = 'wall'; break;
                                case 3: internalType = 'border'; break;
                                case 4: internalType = 'tree'; break;
                                default: internalType = 'grass'; // Default fallback
                            }
                            break;
                        }
                    }
                }
                
                if (row < this.rows && col < this.cols) {
                    this.tiles[row][col].type = internalType;
                    this.tiles[row][col].gid = gid; // Store GID for rendering
                    tilesSet++;
                }
            }
        }
        console.log(`Set ${tilesSet} tiles from layer "${layer.name}"`);
    }

    generateMap() {
        // Initialize all tiles as grass (outside)
        for (let row = 0; row < this.rows; row++) {
            this.tiles[row] = [];
            for (let col = 0; col < this.cols; col++) {
                // Border tiles (non-walkable boundary)
                if (row === 0 || row === this.rows - 1 || col === 0 || col === this.cols - 1) {
                    this.tiles[row][col] = {
                        type: 'border',
                        x: col * this.tileSize,
                        y: row * this.tileSize
                    };
                } else {
                    this.tiles[row][col] = {
                        type: 'grass',
                        x: col * this.tileSize,
                        y: row * this.tileSize
                    };
                }
            }
        }

        // Create house area (centered in world)
        // Make house 3 tiles wider per room (6 tiles total wider)
        // Reduce bottom rows - move house up so there are only 2 tiles between house bottom and map boundary
        const houseStartCol = Math.floor(this.cols / 4);
        const houseHeight = Math.floor(this.rows / 2); // Keep original house height
        // Position house so there are only 2 tiles below it
        const houseStartRow = this.rows - houseHeight - 2; // 2 tiles below house, 1 tile for bottom wall
        const houseWidth = Math.floor(this.cols / 2) + 6; // Added 6 tiles (3 per room)

        // House floor
        for (let row = houseStartRow; row < houseStartRow + houseHeight; row++) {
            for (let col = houseStartCol; col < houseStartCol + houseWidth; col++) {
                if (row < this.rows && col < this.cols) {
                    this.tiles[row][col].type = 'floor';
                }
            }
        }

        // House walls (perimeter)
        for (let col = houseStartCol; col < houseStartCol + houseWidth; col++) {
            if (col < this.cols) {
                // Top wall
                if (houseStartRow < this.rows) {
                    this.tiles[houseStartRow][col].type = 'wall';
                }
                // Bottom wall
                if (houseStartRow + houseHeight - 1 < this.rows) {
                    this.tiles[houseStartRow + houseHeight - 1][col].type = 'wall';
                }
            }
        }

        for (let row = houseStartRow; row < houseStartRow + houseHeight; row++) {
            if (row < this.rows) {
                // Left wall
                if (houseStartCol < this.cols) {
                    this.tiles[row][houseStartCol].type = 'wall';
                }
                // Right wall
                if (houseStartCol + houseWidth - 1 < this.cols) {
                    this.tiles[row][houseStartCol + houseWidth - 1].type = 'wall';
                }
            }
        }

        // Create door openings (remove wall tiles)
        // Doors are now 3 tiles wide (remove one tile on the left)
        const midCol = houseStartCol + Math.floor(houseWidth / 2);
        const doorWidth = 3; // Increased from 2 to 3 tiles

        // Door on top wall
        for (let i = 0; i < doorWidth; i++) {
            const col = midCol + i - Math.floor(doorWidth / 2);
            if (col >= 0 && col < this.cols && houseStartRow < this.rows) {
                this.tiles[houseStartRow][col].type = 'floor';
            }
        }

        // Door on bottom wall
        for (let i = 0; i < doorWidth; i++) {
            const col = midCol + i - Math.floor(doorWidth / 2);
            if (col >= 0 && col < this.cols && houseStartRow + houseHeight - 1 < this.rows) {
                this.tiles[houseStartRow + houseHeight - 1][col].type = 'floor';
            }
        }

        // Add internal room divisions
        const midRow = houseStartRow + Math.floor(houseHeight / 2);

        // Vertical wall dividing rooms
        for (let row = houseStartRow + 1; row < houseStartRow + houseHeight - 1; row++) {
            if (row < this.rows && midCol < this.cols && row !== midRow) {
                // Leave a door gap in the middle
                this.tiles[row][midCol].type = 'wall';
            }
        }

        // Add some trees outside (excluding rightmost columns and bottom area that were removed)
        // Only add trees that are not in the removed areas
        const treePositions = [
            { row: 3, col: 3 },
            { row: Math.floor(this.rows / 2), col: 2 }
        ];

        treePositions.forEach(pos => {
            // Don't place trees in the bottom area (last few rows) or rightmost columns
            if (pos.row > 0 && pos.row < this.rows - 1 &&
                pos.col > 0 && pos.col < this.cols - 1) {
                this.tiles[pos.row][pos.col].type = 'tree';
            }
        });
    }

    getTileAt(x, y) {
        const col = Math.floor(x / this.tileSize);
        const row = Math.floor(y / this.tileSize);

        if (row >= 0 && row < this.rows && col >= 0 && col < this.cols) {
            return this.tiles[row][col];
        }
        return null;
    }

    isWalkable(x, y, size) {
        // Check all four corners of the entity
        const halfSize = size / 2;
        const corners = [
            { x: x - halfSize, y: y - halfSize },
            { x: x + halfSize, y: y - halfSize },
            { x: x - halfSize, y: y + halfSize },
            { x: x + halfSize, y: y + halfSize }
        ];

        for (let corner of corners) {
            const tile = this.getTileAt(corner.x, corner.y);
            if (!tile || !this.tileTypes[tile.type].walkable) {
                return false;
            }
        }

        return true;
    }

    draw(ctx, camera) {
        // Calculate visible tiles
        const startCol = Math.max(0, Math.floor(camera.x / this.tileSize));
        const endCol = Math.min(this.cols, Math.ceil((camera.x + camera.viewWidth) / this.tileSize) + 1);
        const startRow = Math.max(0, Math.floor(camera.y / this.tileSize));
        const endRow = Math.min(this.rows, Math.ceil((camera.y + camera.viewHeight) / this.tileSize) + 1);

        ctx.save();

        // Draw visible tiles
        for (let row = startRow; row < endRow; row++) {
            for (let col = startCol; col < endCol; col++) {
                if (!this.tiles[row] || !this.tiles[row][col]) continue;

                const tile = this.tiles[row][col];
                // Camera is already applied, use world coordinates
                // Round to integer pixels to prevent flickering
                const drawX = Math.round(tile.x);
                const drawY = Math.round(tile.y);

                // If we have tileset images and a GID, draw from tileset
                if (tile.gid && tile.gid > 0 && this.tilesets.length > 0) {
                    this.drawTileFromTileset(ctx, tile.gid, drawX, drawY);
                } else {
                    // Fallback: draw colored rectangle (for procedurally generated maps)
                    const tileInfo = this.tileTypes[tile.type];
                    ctx.fillStyle = tileInfo.color;
                    ctx.fillRect(drawX, drawY, this.tileSize, this.tileSize);

                    // Draw tile border
                    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
                    ctx.lineWidth = 1;
                    ctx.strokeRect(drawX, drawY, this.tileSize, this.tileSize);

                    // Draw bush detail (walkable, decorative)
                    if (tile.type === 'tree') {
                        const centerX = drawX + this.tileSize / 2;
                        const centerY = drawY + this.tileSize / 2;

                        // Draw bush as a low, wide shape (no trunk, just foliage)
                        ctx.fillStyle = '#2E7D32'; // Dark green
                        ctx.beginPath();
                        ctx.ellipse(centerX, centerY + 5, 18, 12, 0, 0, Math.PI * 2);
                        ctx.fill();

                        // Lighter green highlights for texture
                        ctx.fillStyle = '#4CAF50';
                        ctx.beginPath();
                        ctx.ellipse(centerX - 5, centerY + 3, 10, 8, 0, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.beginPath();
                        ctx.ellipse(centerX + 5, centerY + 5, 8, 6, 0, 0, Math.PI * 2);
                        ctx.fill();

                        // Darker green for depth
                        ctx.fillStyle = '#1B5E20';
                        ctx.beginPath();
                        ctx.ellipse(centerX + 3, centerY + 7, 6, 4, 0, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            }
        }

        ctx.restore();
    }
    
    /**
     * Draw a tile from the tileset image based on GID
     * @param {CanvasRenderingContext2D} ctx - Canvas context
     * @param {number} gid - Global Tile ID
     * @param {number} x - World X position
     * @param {number} y - World Y position
     */
    drawTileFromTileset(ctx, gid, x, y) {
        // Find which tileset this GID belongs to
        for (let i = this.tilesets.length - 1; i >= 0; i--) {
            const tileset = this.tilesets[i];
            if (gid >= tileset.firstgid) {
                const localId = gid - tileset.firstgid;
                const tilesetImage = this.tilesetImages.get(tileset.name);
                
                if (!tilesetImage) {
                    // Image not loaded yet, fallback to colored rectangle
                    const tileInfo = this.tileTypes[this.getTileTypeFromGID(gid)];
                    ctx.fillStyle = tileInfo?.color || '#888';
                    ctx.fillRect(x, y, this.tileSize, this.tileSize);
                    return;
                }
                
                // Calculate source position in tileset image
                const tilesPerRow = tileset.columns || Math.floor(tileset.imagewidth / tileset.tilewidth);
                const sourceCol = localId % tilesPerRow;
                const sourceRow = Math.floor(localId / tilesPerRow);
                const sourceX = sourceCol * tileset.tilewidth;
                const sourceY = sourceRow * tileset.tileheight;
                
                // Ensure destination coordinates are integers for pixel-perfect rendering
                const destX = Math.round(x);
                const destY = Math.round(y);
                const destWidth = Math.round(this.tileSize);
                const destHeight = Math.round(this.tileSize);
                
                // Draw the tile from the tileset image
                ctx.drawImage(
                    tilesetImage,
                    sourceX, sourceY, tileset.tilewidth, tileset.tileheight, // Source rectangle
                    destX, destY, destWidth, destHeight // Destination rectangle (rounded)
                );
                return;
            }
        }
        
        // GID not found in any tileset, fallback
        ctx.fillStyle = '#888';
        ctx.fillRect(x, y, this.tileSize, this.tileSize);
    }
    
    /**
     * Get tile type from GID (for fallback rendering)
     */
    getTileTypeFromGID(gid) {
        for (let i = this.tilesets.length - 1; i >= 0; i--) {
            const tileset = this.tilesets[i];
            if (gid >= tileset.firstgid) {
                const localId = gid - tileset.firstgid;
                switch (localId) {
                    case 0: return 'grass';
                    case 1: return 'floor';
                    case 2: return 'wall';
                    case 3: return 'border';
                    case 4: return 'tree';
                    default: return 'grass';
                }
            }
        }
        return 'grass';
    }
}
